import { FC, useEffect, useState, KeyboardEvent } from 'react';
import classNames from 'classnames';
import { Popover, Tooltip } from 'antd';
import { CloseIcon, Filter } from '@dynamika-shared/ui/icons';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { Button } from '@dynamika-shared/ui/Button/Button';

export type RecipientFilterChipProps = {
  active: boolean;
  filterName: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  onClose: () => void;
  maxDisplayWidth?: number;
};

export const RecipientFilterChip: FC<RecipientFilterChipProps> = ({
  active,
  filterName,
  value,
  onChange,
  onClose,
  maxDisplayWidth = 30,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => {
    if (open) setDraft(value ?? '');
  }, [open, value]);

  if (!active) return null;

  const displayText = value ? `${filterName}: ${value}` : filterName;

  const apply = () => {
    onChange(draft.trim() || undefined);
    setOpen(false);
  };

  const clear = () => {
    setDraft('');
    onChange(undefined);
    setOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      apply();
    }
  };

  return (
    <div className='sub-filter-container'>
      <div
        role='button'
        className='group/tag bg-waterLightHover50 hover:bg-waterLight flex h-5 cursor-pointer items-center rounded-full p-1'
      >
        <Popover
          trigger='click'
          placement='bottomLeft'
          open={open}
          onOpenChange={visible => setOpen(visible)}
          content={
            <div className='flex w-[22.4rem] flex-col gap-1 p-1'>
              <div className='text-1216 text-spanishGray'>
                Set up your Filters
              </div>
              <Input
                autoFocus
                value={draft}
                placeholder='Name or email'
                onChange={e => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <div className='flex items-center justify-end gap-1'>
                <Button
                  onClick={clear}
                  className='!bg-transparent !text-dimGray'
                >
                  Clear
                </Button>
                <Button onClick={apply}>Apply</Button>
              </div>
            </div>
          }
          styles={{ body: { padding: 0 } }}
        >
          <div className='flex items-center justify-center gap-1 p-1'>
            <Filter
              height={20}
              width={20}
              className='group-hover/tag:text-dodgerBlue'
            />
            <Tooltip
              color='white'
              title={
                displayText.length > 50 ? (
                  <span className='text-1424 text-black'>{displayText}</span>
                ) : undefined
              }
            >
              <div
                className={classNames(
                  'text-1424 text-black group-hover/tag:text-dodgerBlue truncate overflow-hidden text-ellipsis whitespace-nowrap'
                )}
                style={
                  maxDisplayWidth > 0
                    ? { maxWidth: `${maxDisplayWidth}rem` }
                    : undefined
                }
              >
                {displayText}
              </div>
            </Tooltip>
            <CloseIcon
              width={20}
              height={20}
              className='rounded-sm hover:bg-water hover:text-dodgerBlue'
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
            />
          </div>
        </Popover>
      </div>
    </div>
  );
};
